import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "./firebase";
import {
  collection, addDoc, query, where, getDocs,
  doc, updateDoc, arrayUnion, serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "./App";

function generateJoinCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function Toggle({ label, description, value, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{label}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, lineHeight: 1.4 }}>{description}</div>
      </div>
      <button onClick={() => onChange(!value)} style={{
        width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer",
        background: value ? "#FF6B35" : "rgba(255,255,255,0.15)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%", background: "white",
          position: "absolute", top: 3, left: value ? 25 : 3,
          transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }} />
      </button>
    </div>
  );
}

export default function UEContestHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myContests, setMyContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [tab, setTab] = useState("my");

  // Handle ?join=CODE invite link — also check sessionStorage fallback for post-auth redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const joinParam = params.get("join");

    // Also check sessionStorage in case URL param was lost during auth redirect
    let code = joinParam;
    if (!code) {
      try {
        const stored = sessionStorage.getItem("ue_pendingJoinCode");
        if (stored) { code = stored; sessionStorage.removeItem("ue_pendingJoinCode"); }
      } catch (e) {}
    }

    if (code) {
      setJoinCode(code.toUpperCase());
      setTab("join");
    }
  }, [location.search]);

  // Create form
  const [contestName, setContestName] = useState("");
  const [contestMessage, setContestMessage] = useState("");
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [openInvite, setOpenInvite] = useState(true);
  const [maxEntries, setMaxEntries] = useState(3);
  const [creating, setCreating] = useState(false);

  // Join form
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => { fetchMyContests(); }, []);

  const fetchMyContests = async () => {
    setLoading(true);
    const q = query(
      collection(db, "ue_contests"),
      where("members", "array-contains", user.uid)
    );
    const snap = await getDocs(q);
    setMyContests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const createContest = async () => {
    if (!contestName.trim()) return;
    setCreating(true);
    try {
      const code = generateJoinCode();
      const ref = await addDoc(collection(db, "ue_contests"), {
        name: contestName.trim(),
        message: contestMessage.trim(),
        adminUid: user.uid,
        adminName: user.displayName,
        joinCode: code,
        season: "2026",
        allowMultipleEntries: allowMultiple,
        openInvite,
        maxEntriesPerUser: allowMultiple ? maxEntries : 1,
        members: [user.uid],
        createdAt: serverTimestamp(),
      });
      navigate(`contest/${ref.id}`);
    } catch (e) {
      console.error("Error creating contest:", e);
    }
    setCreating(false);
  };

  const joinContest = async () => {
    setJoinError("");
    if (!joinCode.trim()) { setJoinError("Enter a join code."); return; }
    setJoining(true);
    try {
      const q = query(
        collection(db, "ue_contests"),
        where("joinCode", "==", joinCode.toUpperCase().trim())
      );
      const snap = await getDocs(q);
      if (snap.empty) { setJoinError("No pool found with that code."); setJoining(false); return; }
      const contestDoc = snap.docs[0];
      await updateDoc(doc(db, "ue_contests", contestDoc.id), {
        members: arrayUnion(user.uid),
      });
      navigate(`contest/${contestDoc.id}`);
    } catch (e) {
      setJoinError("Something went wrong. Try again.");
    }
    setJoining(false);
  };

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div style={{
        background: "linear-gradient(135deg, #0d1620 0%, #131f35 100%)",
        border: "1px solid rgba(255,107,53,0.2)",
        borderRadius: 20, marginBottom: 24, overflow: "hidden", position: "relative",
      }}>
        {/* Hash mark pattern */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.025,
          backgroundImage: "repeating-linear-gradient(90deg, white 0px, white 2px, transparent 2px, transparent 40px)",
          pointerEvents: "none",
        }} />
        <div style={{ padding: "28px 28px", position: "relative" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#FF6B35", marginBottom: 6, textTransform: "uppercase" }}>
            2026 NFL Season
          </div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "white", letterSpacing: 2, lineHeight: 1, marginBottom: 10 }}>
            WELCOME BACK,&nbsp;<span style={{ color: "#FF6B35" }}>{(user.displayName || "PLAYER").toUpperCase()}</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, marginBottom: 20, maxWidth: 480 }}>
            Pick one underdog every week. Earn points equal to the spread when they pull the upset. Season-long glory awaits.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700 }} onClick={() => setTab("create")}>
              + Create Pool
            </button>
            <button className="btn btn-outline btn-sm" style={{ borderColor: "#FF6B35", color: "#FF6B35" }} onClick={() => setTab("join")}>
              Join Pool
            </button>
          </div>
        </div>
      </div>

      {/* How it works strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 12, marginBottom: 28,
      }}>
        {[
          { icon: "🎯", label: "Pick One Dog", detail: "One underdog per entry each week" },
          { icon: "🏆", label: "Win Outright", detail: "Dog must win the game straight up" },
          { icon: "📊", label: "Earn the Spread", detail: "Points = closing spread value" },
          { icon: "🔁", label: "Repeat Teams", detail: "No team restrictions week-to-week" },
        ].map((item) => (
          <div key={item.label} className="card" style={{ textAlign: "center", padding: "14px 12px", borderColor: "rgba(255,107,53,0.15)" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "white", marginBottom: 3 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.4 }}>{item.detail}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="nav-tabs" style={{ "--tab-active-bg": "#FF6B35" }}>
        {["my", "create", "join"].map((t) => (
          <button
            key={t}
            className={"nav-tab" + (tab === t ? " active" : "")}
            onClick={() => setTab(t)}
            style={tab === t ? { background: "#FF6B35", color: "white" } : {}}
          >
            {t === "my" ? "My Pools" : t === "create" ? "Create Pool" : "Join Pool"}
          </button>
        ))}
      </div>

      {/* My Pools */}
      {tab === "my" && (
        <div>
          {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading...</p>}
          {!loading && myContests.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 20px", color: "rgba(255,255,255,0.35)" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🏈</div>
              <p style={{ marginBottom: 16 }}>No pools yet — create or join one to get started.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700 }} onClick={() => setTab("create")}>Create a Pool</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab("join")}>Join a Pool</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myContests.map((c) => (
              <div
                key={c.id}
                className="card"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderLeft: "4px solid #FF6B35", cursor: "pointer" }}
                onClick={() => navigate(`contest/${c.id}`)}
              >
                <div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 1 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span>🏈 2026 Season</span>
                    {c.openInvite && <span>Code: <span style={{ color: "#FF6B35", letterSpacing: 2, fontWeight: 700 }}>{c.joinCode}</span></span>}
                    {c.allowMultipleEntries && <span className="badge badge-gray">Up to {c.maxEntriesPerUser} entries</span>}
                  </div>
                </div>
                <button
                  className="btn btn-sm"
                  style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}
                  onClick={(e) => { e.stopPropagation(); navigate(`contest/${c.id}`); }}
                >
                  Enter →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Pool */}
      {tab === "create" && (
        <div className="card" style={{ padding: 28, maxWidth: 560 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#FF6B35", letterSpacing: 2, marginBottom: 20 }}>
            CREATE A POOL
          </div>

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 6 }}>POOL NAME</label>
          <input value={contestName} onChange={(e) => setContestName(e.target.value)} placeholder="e.g. Office Dogs 2026" className="input" style={{ marginBottom: 16 }} />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 6 }}>
            POOL MESSAGE <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            value={contestMessage} onChange={(e) => setContestMessage(e.target.value)}
            placeholder="e.g. Buy-in is $25, Venmo @you. Season winner takes all!"
            className="input" maxLength={500} style={{ minHeight: 80, resize: "vertical", lineHeight: 1.5, marginBottom: 20 }}
          />

          <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 4 }}>POOL SETTINGS</div>
          <Toggle
            label="Multiple Entries"
            description="Allow players to submit more than one entry per contest."
            value={allowMultiple}
            onChange={setAllowMultiple}
          />
          {allowMultiple && (
            <div style={{ padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "white", marginBottom: 8 }}>Max Entries Per Player</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[2, 3, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setMaxEntries(n)}
                    style={{
                      padding: "6px 16px", borderRadius: 8, border: "1px solid",
                      borderColor: maxEntries === n ? "#FF6B35" : "rgba(255,255,255,0.15)",
                      background: maxEntries === n ? "rgba(255,107,53,0.2)" : "transparent",
                      color: maxEntries === n ? "#FF6B35" : "rgba(255,255,255,0.5)",
                      fontWeight: 700, fontSize: 14, cursor: "pointer",
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Toggle
            label="Open Invites"
            description="Members can share the join code and invite others. Turn off to restrict invites to you only."
            value={openInvite}
            onChange={setOpenInvite}
          />

          <button
            onClick={createContest}
            disabled={creating || !contestName.trim()}
            className="btn btn-full btn-lg"
            style={{ background: "#FF6B35", color: "white", fontWeight: 700, borderRadius: 10, marginTop: 24 }}
          >
            {creating ? "Creating..." : "🏈 Create Pool"}
          </button>
        </div>
      )}

      {/* Join Pool */}
      {tab === "join" && (
        <div className="card" style={{ padding: 28, maxWidth: 400 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#FF6B35", letterSpacing: 2, marginBottom: 20 }}>
            JOIN A POOL
          </div>
          <input
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && joinContest()}
            placeholder="Enter 6-character code"
            className="input"
            style={{ marginBottom: 12, letterSpacing: 4, textTransform: "uppercase" }}
          />
          {joinError && <p className="error-text" style={{ marginBottom: 10 }}>{joinError}</p>}
          <button
            onClick={joinContest}
            disabled={joining}
            className="btn btn-full btn-lg"
            style={{ background: joining ? "rgba(255,107,53,0.6)" : "#FF6B35", color: "white", fontWeight: 700, borderRadius: 10, transition: "background 0.2s" }}
          >
            {joining ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: "spin-slow 1s linear infinite" }}>
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round"/>
                </svg>
                Looking up your pool...
              </span>
            ) : "Join Pool →"}
          </button>
          {joining && (
            <p style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 10 }}>
              Hang tight — this takes a few seconds
            </p>
          )}
        </div>
      )}
    </div>
  );
}
